import { useId, useState } from "react";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/loading-button";
import { ComposeTextarea } from "@/components/compose-field";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { todayIso } from "@/features/owed-items/model";
import { useRecordExpediteCall, type ExpediteCallTerms } from "./hooks";

const TERMS_OPTIONS: ReadonlyArray<{ value: ExpediteCallTerms; label: string }> = [
  { value: "net", label: "Net terms" },
  { value: "prepay", label: "Prepay" },
  { value: "card", label: "Card" },
  { value: "unknown", label: "Not sure" },
];

/**
 * What a call to expedite a Service PO settled (USA-1192): the ship date, our payment terms and
 * anything else they said. Recording it closes the item and files the call on the PO's conversation.
 */
export function ExpediteCallOutcome({
  poId,
  actionId,
  onCancel,
}: {
  poId: string;
  actionId: number;
  onCancel: () => void;
}) {
  const id = useId();
  const record = useRecordExpediteCall(poId);
  const [shipDate, setShipDate] = useState("");
  const [terms, setTerms] = useState<ExpediteCallTerms | null>(null);
  const [notes, setNotes] = useState("");
  const ready = Boolean(shipDate || terms || notes.trim());

  return (
    <form
      className="space-y-3 border-t pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        record.mutate({ actionId, shipDate: shipDate || null, terms, notes: notes.trim() });
      }}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 space-y-1 sm:w-44">
          <Label htmlFor={`${id}-ship`}>Ship date</Label>
          <Input
            id={`${id}-ship`}
            type="date"
            value={shipDate}
            min={todayIso()}
            onChange={(event) => setShipDate(event.target.value)}
            autoFocus
          />
        </div>
        <div className="min-w-0 space-y-1">
          <span className="block text-sm leading-none font-medium">Payment terms</span>
          <ReviewChoiceGroup
            label="Payment terms"
            value={terms}
            options={TERMS_OPTIONS}
            onChange={setTerms}
            disabled={record.isPending}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${id}-notes`}>What did they say?</Label>
        <ComposeTextarea
          id={`${id}-notes`}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={3}
          className="text-sm"
        />
      </div>
      <div className="flex gap-2">
        <LoadingButton
          type="submit"
          size="sm"
          loading={record.isPending}
          loadingText="Recording..."
          disabled={!ready}
        >
          <Phone className="size-3.5" aria-hidden="true" /> Record call
        </LoadingButton>
        <Button type="button" size="sm" variant="ghost" disabled={record.isPending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
